import { Stack, TextField } from '@mui/material';
import { useState } from 'react';
import { FilterableSelect } from '../common/FilterableSelect';

export const STYLE_PRESETS = [
  { value: 'watercolor', label: 'Watercolor Illustration' },
  { value: 'digital-painting', label: 'Digital Painting' },
  { value: 'graphic-novel', label: 'Graphic Novel / Comic' },
  { value: 'photorealistic', label: 'Photorealistic' },
  { value: 'anime', label: 'Anime / Manga' },
  { value: 'fantasy-art', label: 'Fantasy Art' },
  { value: 'oil-painting', label: 'Oil Painting' },
  { value: 'pencil-sketch', label: 'Pencil Sketch' },
  { value: 'custom', label: 'Custom...' },
] as const;

interface StylePresetSelectorProps {
  value: string;
  onChange: (value: string) => void;
}

/**
 * Reusable style preset dropdown for image generation.
 * When "Custom" is selected, shows a free-text input for arbitrary styles.
 */
export function StylePresetSelector({
  value,
  onChange,
}: StylePresetSelectorProps) {
  const isKnownPreset = STYLE_PRESETS.some(
    (p) => p.value === value && p.value !== 'custom',
  );
  const isCustom = !isKnownPreset && value !== '';
  const [customText, setCustomText] = useState(isCustom ? value : '');
  const selectValue = isCustom ? 'custom' : value;

  const handleSelectChange = (next: string) => {
    if (next === 'custom') {
      onChange(customText);
    } else {
      onChange(next);
    }
  };

  const handleCustomTextChange = (text: string) => {
    setCustomText(text);
    onChange(text);
  };

  const options = STYLE_PRESETS.map((p) => ({
    value: p.value,
    label: p.label,
  }));

  return (
    <Stack spacing={1.5}>
      <FilterableSelect
        options={options}
        value={selectValue}
        onChange={handleSelectChange}
        label="Style preset"
        allowEmpty
        emptyLabel="None"
      />
      {selectValue === 'custom' && (
        <TextField
          label="Custom style"
          placeholder="e.g., Impressionist watercolor with gold leaf accents"
          value={customText}
          onChange={(e) => handleCustomTextChange(e.target.value)}
          size="small"
          fullWidth
        />
      )}
    </Stack>
  );
}
